<template>
  <div class="date-picker-root" :style="getRootStyle">
    <div class="form-label label-text" v-if="label">
      {{ $t(label) }}
      <span v-if="is_required" class="req-sign">*</span>
    </div>
    <validation-provider
      :name="name"
      :rules="rules"
      v-slot="{ errors }"
      :id="id"
    >
      <input type="text" v-model="inputfield" class="d-none" />
      <div
        class="form-control disabled"
        :style="cus_style"
        v-if="disabled || readOnly"
      >
        <span style="font-size: 14px">
          <span v-if="singleDatePicker">
            <span v-if="dateRange.startDate">
              {{ dateRange.startDate | moment("DD-MM-YYYY, H:mm") }}
            </span>
            <span v-else-if="placeholder" style="color: var(--color-83)">
              {{ $t(placeholder) }}
            </span>
            <span v-else-if="label" style="color: var(--color-83)">
              {{ $t(label) }}
            </span>
            <span v-else>DD-MM-YYYY, H:mm</span>
          </span>
          <span v-else>
            <span v-if="dateRange.startDate && dateRange.endDate">
              <span>
                {{ dateRange.startDate | moment("DD-MM-YYYY, H:mm") }}
              </span>
              <span> - </span>
              <span>
                {{ dateRange.endDate | moment("DD-MM-YYYY, H:mm") }}
              </span>
            </span>
            <span v-else-if="placeholder" style="color: var(--color-83)">
              {{ $t(placeholder) }}
            </span>
            <span v-else-if="label" style="color: var(--color-83)">
              {{ $t(label) }}
            </span>
            <span v-else>DD-MM-YYYY, H:mm - DD-MM-YYYY, H:mm</span>
          </span>
        </span>
      </div>
      <date-range-picker
        v-else
        :class="[
          {
            'invalid-range':
              is_invalid || (errors && errors.length) ? true : false,
            disabled: disabled,
          },
        ]"
        ref="picker"
        :locale-data="locale_data"
        :opens="isLangRtl ? 'left' : 'right'"
        :minDate="minDate"
        :maxDate="maxDate"
        :singleDatePicker="singleDatePicker"
        :ranges="false"
        :timePicker="timePicker"
        :timePicker24Hour="true"
        :showWeekNumbers="false"
        :showDropdowns="false"
        :autoApply="false"
        v-model="dateRange"
        @update="updateValues"
        :disabled="disabled ? true : false"
      >
        <template v-slot:input="picker">
          <span style="font-size: 14px">
            <span v-if="singleDatePicker">
              <span v-if="picker.startDate">
                {{ picker.startDate | moment("DD-MM-YYYY, H:mm") }}
              </span>
              <span v-else-if="placeholder" style="color: var(--color-83)">
                {{ $t(placeholder) }}
              </span>
              <span v-else-if="label" style="color: var(--color-83)">
                {{ $t(label) }}
              </span>
              <span v-else>DD-MM-YYYY, H:mm</span>
            </span>
            <span v-else>
              <span v-if="picker.startDate && picker.endDate">
                <span>
                  {{ picker.startDate | moment("DD-MM-YYYY, H:mm") }}
                </span>
                <span> - </span>
                <span>
                  {{ picker.endDate | moment("DD-MM-YYYY, H:mm") }}
                </span>
              </span>
              <span v-else-if="placeholder" style="color: var(--color-83)">
                {{ $t(placeholder) }}
              </span>
              <span v-else-if="label" style="color: var(--color-83)">
                {{ $t(label) }}
              </span>
              <span v-else>DD-MM-YYYY, H:mm - DD-MM-YYYY, H:mm</span>
            </span>
          </span>
        </template>
      </date-range-picker>

      <div v-if="helper_text" class="helper-text" :style="helper_text_style">
        {{ $t(helper_text) }}
      </div>
      <div
        class="error-text"
        :style="helper_text_style"
        v-if="errors && errors.length"
      >
        {{ errors[0] }}
      </div>
      <slot name="attachment"></slot>
    </validation-provider>
  </div>
</template>

<script>
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

export default {
  emits: ["change-value", "change"],
  props: [
    "singleDatePicker",
    "timePicker",
    "label",
    "placeholder",
    "is_invalid",
    "helper_text",
    "helper_text_style",
    "from_date_field",
    "to_date_field",
    "from_time_field",
    "to_time_field",
    "value",
    "cus_style",
    "remove_default_margin",
    "readOnly",
    "disabled",
    "cus_ref",
    "is_required",
    "options",
    "rules",
    "name",
    "id",
    "not_future_date",
    "minDate",
    "maxDate",
  ],
  components: { DateRangePicker },
  data() {
    return {
      inputfield: null,
      dateRange: {
        startDate: null,
        endDate: null,
      },
      locale_data: {
        firstDay: 0,
        direction: this.isLangRtl ? "rtl" : "ltr",
        separator: " - ",
        applyLabel: this.$t("save"),
        cancelLabel: this.$t("cancel"),
        daysOfWeek: [
          this.$t("Sun"),
          this.$t("Mon"),
          this.$t("Tue"),
          this.$t("Wed"),
          this.$t("Thu"),
          this.$t("Fri"),
          this.$t("Sat"),
        ],
        monthNames: [
          this.$t("January"),
          this.$t("February"),
          this.$t("March"),
          this.$t("April"),
          this.$t("May"),
          this.$t("June"),
          this.$t("July"),
          this.$t("August"),
          this.$t("September"),
          this.$t("October"),
          this.$t("November"),
          this.$t("December"),
        ],
      },
    };
  },
  computed: {
    getRootStyle() {
      return `margin-bottom: ${
        this.remove_default_margin ? "0px" : "20px"
      };position:relative;`;
    },
  },
  methods: {
    updateValues(val) {
      let from_date_value, to_date_value, from_time_value, to_time_value;
      if (val?.startDate) {
        let temp = val.startDate;
        from_time_value = moment(temp).locale("en").format("HH:mm:ss");
        from_date_value = new Date(new Date(temp).setHours(0, 0, 0, 0));
      }
      if (val?.endDate) {
        let temp = val.endDate;
        to_time_value = moment(temp).locale("en").format("HH:mm:ss");
        to_date_value = new Date(new Date(temp).setHours(0, 0, 0, 0));
      }
      this.$emit(
        "change-value",
        from_date_value,
        this.from_date_field,
        to_date_value,
        this.to_date_field,
        from_time_value,
        this.from_time_field,
        to_time_value,
        this.to_time_field
      );
      this.$emit(
        "change",
        from_date_value,
        to_date_value,
        from_time_value,
        to_time_value
      );
    },
    setDefaultValue(value) {
      let startDate, endDate;
      if (value[this.from_date_field])
        startDate = new Date(value[this.from_date_field]);
      if (value[this.from_time_field])
        startDate = startDate.setHours(
          value[this.from_time_field].substring(0, 2),
          value[this.from_time_field].substring(3, 5),
          value[this.from_time_field].substring(6, 8)
        );

      if (value[this.to_date_field])
        endDate = new Date(value[this.to_date_field]);
      if (value[this.to_time_field])
        endDate = endDate.setHours(
          value[this.to_time_field].substring(0, 2),
          value[this.to_time_field].substring(3, 5),
          value[this.to_time_field].substring(6, 8)
        );

      this.dateRange = { startDate: startDate, endDate: endDate };
    },
  },
  watch: {
    dateRange(val) {
      this.inputfield = val?.startDate;
    },
  },
  mounted() {
    this.setDefaultValue(this.value);
  },
  i18n: {
    messages: {
      en: require("@/services/i18n/locales/en/reservationOperations.json"),
      ar: require("@/services/i18n/locales/ar/reservationOperations.json"),
    },
  },
};
</script>

<style lang="scss" scoped>
.label-text {
  font-size: 14px;
  color: var(--color-61);
  margin-bottom: 5px;
  font-weight: 700;
}

</style>

<style lang="scss">
.vue-daterange-picker {
  width: 100%;
}
.reportrange-text {
  width: 100%;
  height: 40px;
  font-size: 14px;
  padding: 10px;
  background: var(--color-01);
  color: var(--color-61);
  border: 1px solid;
  border-radius: 6px;
  border-color: var(--color-64);
  box-shadow: 0px 1px 2px var(--color-37);
  font-weight: 400;

  &:focus-visible {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:hover {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:focus {
    border-color: var(--color-04);
    outline: none;
    box-shadow: none;
  }
  &:disabled {
    background-color: var(--color-67);
  }
}
.disabled .reportrange-text {
  border-color: var(--color-67) !important;
  background-color: var(--color-67) !important;
}
.invalid-range .reportrange-text {
  border-color: var(--color-47) !important;
  background-color: var(--color-01) !important;
}
.daterangepicker td.active,
.daterangepicker td.active:hover {
  background-color: var(--color-23) !important;
  color: var(--color-61) !important;
}
.daterangepicker td.in-range {
  background-color: var(--color-64);
  color: var(--color-61);
}
</style>
